import React, { useState, useEffect } from 'react';
import { marketingApi, WhatsAppTemplate } from '../services/marketingApi';

interface TemplatePickerProps {
  conversationId?: string;
  onTemplateSelected: (template: WhatsAppTemplate) => void;
  onClose: () => void;
}

const TemplatePicker: React.FC<TemplatePickerProps> = ({ conversationId, onTemplateSelected, onClose }) => {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [reasoning, setReasoning] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTemplates();
  }, [conversationId]);

  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      if (conversationId) {
        // Use intelligent suggestions based on conversation
        const response = await marketingApi.composeTemplates(conversationId);
        setTemplates(response.templates);
        setReasoning(response.reasoning);
      } else {
        // Fallback to basic template list
        const data = await marketingApi.getTemplates('IN_SESSION', 'APPROVED');
        setTemplates(data);
        setReasoning('');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load templates. Please try again.');
    }
    setLoading(false);
  };

  const getTemplateTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'IMAGE':
        return 'bg-sky-wash/40 text-deep-sky';
      case 'VIDEO':
        return 'bg-brick/10 text-brick';
      case 'CAROUSEL':
        return 'bg-sky-wash/40 text-deep-sky';
      case 'TEXT_HEADER':
        return 'bg-gold/15 text-amber-ink';
      case 'TEXT':
      default:
        return 'bg-cream text-charcoal';
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const renderPreview = (template: WhatsAppTemplate) => {
    // IMAGE type with media URL
    if (template.template_type === 'IMAGE' && template.header_media_url) {
      return (
        <div className="w-full h-[70px] bg-charcoal/10 rounded overflow-hidden">
          <img
            src={template.header_media_url}
            alt={template.display_name}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to placeholder on error
              (e.target as HTMLImageElement).style.display = 'none';
              (e.target as HTMLImageElement).parentElement!.innerHTML = `
                <div class="w-full h-full flex items-center justify-center bg-charcoal/10">
                  <svg class="w-8 h-8 text-graphite/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              `;
            }}
          />
        </div>
      );
    }

    // VIDEO type with media URL
    if (template.template_type === 'VIDEO' && template.header_media_url) {
      return (
        <div className="w-full h-[70px] bg-charcoal/10 rounded overflow-hidden relative">
          <img
            src={template.header_media_url}
            alt={template.display_name}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
              (e.target as HTMLImageElement).parentElement!.innerHTML = `
                <div class="w-full h-full flex items-center justify-center bg-charcoal/10">
                  <svg class="w-8 h-8 text-graphite/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
              `;
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-black/50 rounded-full p-2">
              <svg className="w-6 h-6 text-warm-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </div>
      );
    }

    // IMAGE type without media URL - show placeholder
    if (template.template_type === 'IMAGE' && !template.header_media_url) {
      return (
        <div className="w-full h-[70px] bg-charcoal/10 rounded flex items-center justify-center">
          <svg className="w-8 h-8 text-graphite/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </div>
      );
    }

    // VIDEO type without media URL - show placeholder
    if (template.template_type === 'VIDEO' && !template.header_media_url) {
      return (
        <div className="w-full h-[70px] bg-charcoal/10 rounded flex items-center justify-center">
          <svg className="w-8 h-8 text-graphite/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        </div>
      );
    }

    // CAROUSEL type
    if (template.template_type === 'CAROUSEL' && template.carousel_cards && template.carousel_cards.length > 0) {
      const firstCard = template.carousel_cards[0];
      if (firstCard.header_media_url) {
        return (
          <div className="w-full h-[70px] bg-charcoal/10 rounded overflow-hidden relative">
            <img
              src={firstCard.header_media_url}
              alt={template.display_name}
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <div className="absolute bottom-1 right-1 bg-black/60 text-warm-white text-xs px-1.5 py-0.5 rounded">
              {template.carousel_cards.length} cards
            </div>
          </div>
        );
      }
    }

    // CAROUSEL type without media - show carousel icon
    if (template.template_type === 'CAROUSEL') {
      return (
        <div className="w-full h-[70px] bg-sky-wash/20 rounded flex items-center justify-center">
          <div className="flex items-center gap-1">
            <div className="w-8 h-10 bg-sky-wash/60 rounded"></div>
            <div className="w-8 h-10 bg-sky-wash/60 rounded"></div>
            <div className="w-8 h-10 bg-sky-wash/60 rounded"></div>
          </div>
        </div>
      );
    }

    // TEXT_HEADER - show with header icon
    if (template.template_type === 'TEXT_HEADER') {
      return (
        <div className="w-full h-[70px] bg-gold/10 rounded p-2 overflow-hidden flex flex-col">
          <div className="flex items-center gap-1 mb-1">
            <svg className="w-3 h-3 text-amber-ink" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 5h18v2H3V5zm0 6h18v2H3v-2zm0 6h18v2H3v-2z" />
            </svg>
            <span className="text-[9px] text-amber-ink font-semibold">HEADER</span>
          </div>
          <p className="text-xs text-graphite line-clamp-3 flex-1">
            {template.body}
          </p>
        </div>
      );
    }

    // TEXT or fallback - show text preview
    return (
      <div className="w-full h-[70px] bg-cream rounded p-2 overflow-hidden">
        <p className="text-xs text-graphite line-clamp-4">
          {template.body}
        </p>
      </div>
    );
  };

  return (
    <div className="bg-warm-white border border-sketch rounded-lg shadow-sketch-lg">
      {/* Header */}
      <div className="p-3 border-b border-sketch flex items-center justify-between">
        <h4 className="text-charcoal text-sm font-semibold">Templates</h4>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchTemplates}
            disabled={loading}
            className="p-1 text-graphite hover:text-charcoal hover:bg-cream rounded transition-colors disabled:opacity-50"
            title="Refresh templates"
          >
            <svg
              className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
          <button
            onClick={onClose}
            className="px-2 py-1 text-xs text-graphite hover:text-charcoal hover:bg-cream rounded transition-colors"
          >
            Hide
          </button>
        </div>
      </div>

      {/* Templates List */}
      <div className="p-3">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="flex flex-col items-center">
              <svg
                className="w-8 h-8 text-forest animate-spin mb-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <p className="text-sm text-graphite">Loading templates...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-8">
            <div className="flex flex-col items-center">
              <svg
                className="w-8 h-8 text-brick mb-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-sm text-brick mb-2">{error}</p>
              <button
                onClick={fetchTemplates}
                className="text-xs text-forest hover:underline"
              >
                Try again
              </button>
            </div>
          </div>
        ) : templates.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <div className="flex flex-col items-center">
              <svg
                className="w-8 h-8 text-graphite/70 mb-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="text-sm text-graphite">No templates available</p>
            </div>
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-2">
            {templates.map((template, index) => {
              const isRecommended = index === 0 && template.suggested_message;
              return (
                <div
                  key={template.id || template.name}
                  onClick={() => onTemplateSelected(template)}
                  className={`flex-shrink-0 w-[160px] border rounded-lg p-2 cursor-pointer hover:shadow-md transition-all ${
                    isRecommended
                      ? 'border-forest border-2 bg-forest/10'
                      : 'border-sketch hover:border-forest'
                  }`}
                >
                  {/* Recommended Badge */}
                  {isRecommended && (
                    <div className="bg-forest text-warm-white text-xs px-2 py-0.5 rounded mb-2 inline-block">
                      ✨ Recommended
                    </div>
                  )}

                  {/* Preview */}
                  <div className="mb-2">
                    {renderPreview(template)}
                  </div>

                  {/* Display Name */}
                  <h5 className="text-xs font-semibold text-charcoal mb-1 truncate" title={template.display_name}>
                    {template.display_name}
                  </h5>

                  {/* Suggested Message (for recommended) */}
                  {isRecommended && template.suggested_message && (
                    <p className="text-xs text-forest mb-2 line-clamp-2 italic">
                      "{template.suggested_message}"
                    </p>
                  )}

                  {/* Body Preview (for others) */}
                  {!isRecommended && (
                    <p className="text-xs text-graphite mb-2 line-clamp-2" title={template.body}>
                      {template.body}
                    </p>
                  )}

                  {/* Type Badge and Features */}
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className={`inline-block text-xs px-2 py-0.5 rounded ${getTemplateTypeBadgeColor(template.template_type)}`}>
                      {template.template_type}
                    </span>
                    {template.has_buttons && (
                      <span className="text-xs text-deep-sky">📱</span>
                    )}
                    {template.has_carousel && (
                      <span className="text-xs text-deep-sky">🎠</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplatePicker;
